import React, { useEffect, useState, useRef } from 'react'

import clsx from 'clsx'

import ImpressionEvent from 'components/ImpressionEvent'
import Link from 'components/Link'
import Typography from 'components/Typography'
import { useAppContext } from 'context/AppContext'
import { useEventContext } from 'context/EventContext'
import useViewport from 'hooks/useViewport'
import { handleScrollTo } from 'screens/compare/components/NavigationList/helpers'

import PPLBanner from './components/PPLBanner'

import classes from './TableOfContent.module.scss'

/** @type {import("./types").default} */
const TableOfContent = ({ content, stickyContainerRef = null }) => {
  const { addEvent } = useEventContext()
  const [selectedMenuItem, setSelectedMenuItem] = useState(null)
  const { isCypressTakingSnapshots, head } = useAppContext()
  const hasPPLExpertForm = head?.hasPplExpertFormEnabled
  const { up } = useViewport()
  const isDesktop = up('md')

  useEffect(() => {
    if (!isDesktop) return
    const observerCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target?.id) {
          setSelectedMenuItem(entry.target.id)
          return
        }
      })
    }
    const observer = new IntersectionObserver(observerCallback, { rootMargin: '-25% 0px -40% 0px' })

    document.querySelectorAll('.ContentItem').forEach(element => observer.observe(element))

    return () => observer.disconnect()
  }, [isDesktop])

  const defaultRef = useRef()
  const containerRef = stickyContainerRef || defaultRef

  useEffect(() => {
    if (isDesktop && !isCypressTakingSnapshots) {
      const setSticky = async () => {
        const StickyFilter = (await import('components/Filters/stickyFilter.js')).default
        // This is needed because after waiting for the import sometimes the ref is null
        if (containerRef.current)
          new StickyFilter(containerRef.current, {
            topSpacing: 94,
            bottomSpacing: 80,
          })
      }

      if (containerRef.current) {
        setSticky()
      }
    }
  }, [containerRef, isDesktop, isCypressTakingSnapshots])

  return (
    <div className={classes.root} ref={defaultRef}>
      <Typography variant="heading-medium" className={classes.header}>
        Table of Contents
      </Typography>
      <div className={classes.itemsList}>
        {content.map(({ title = '', menuTitle = '', id }) => (
          <Link
            key={id}
            onClick={() => handleScrollTo(id, 100)}
            className={clsx(classes.item, { [classes.active]: selectedMenuItem === id })}
            {...addEvent({
              name: 'engagement_navigation',
              engagementComponent: 'table-of-contents',
              engagementDestination: `jump-to_${id}`,
              engagementDetails: `text-link_${id}`,
            })}
          >
            {menuTitle || title}
          </Link>
        ))}
      </div>

      {hasPPLExpertForm && isDesktop && (
        <ImpressionEvent
          event={{
            engagementComponent: 'ppl_banner',
            engagementDestination: 'banner_displayed',
            engagementDetails: 'ppl_banner',
            name: 'section_impression',
          }}
        >
          <PPLBanner />
        </ImpressionEvent>
      )}
    </div>
  )
}

export default TableOfContent
